import React, { useCallback, useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import { APIProvider } from "@vis.gl/react-google-maps";

// Imported assets
import blueIcon from "../../../assets/images/map/BlueIconNoLightning_Small.png";
import CaretDown from "../../../assets/icons/DownCarrot_WhiteIcon.svg";
import CaretUp from "../../../assets/icons/UpCarrot_WhiteIcon.svg";
import greenIcon from "../../../assets/images/map/GreenIconNoLightning_Small.png";
import HurricaneIcon from "../../../assets/icons/Hurricane_Icon.png";
import redIcon from "../../../assets/images/map/RedIconNoLightning_Small.png";
import terbineCNMPLogo from "../../../assets/images/MissionControl_LogoWhite.png";
import WildfireIcon from "../../../assets/icons/Wildfire_Icon.png";
import yellowIcon from "../../../assets/images/map/YellowIconNoLightning_Small.png";

// Imported components
import Map from "./components/Map";
import NavigationBar from "../../../components/nav/NavigationBar";

// Imported contexts
import { useUserContext } from "../../../contexts/User";

// Keyframes
const breatheAnimation = keyframes`
  0% { scale: 1; }
  50% { scale: 1.25; }
  100% { scale: 1; }
`;

// Styled components
const Container = styled.div`
  align-items: center;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  gap: 0px;
  height: 100vh; /* Ensure the container takes up the full viewport height */
  justify-content: flex-start;
  position: relative;
`;

const Icon = styled.img`
  height: auto;
  width: 16px;
`;

const MapCanvas = styled.div`
  display: flex; /* Ensure MapComponent can expand within */
  flex-grow: 1; /* Allow MapCanvas to take up remaining space */
  width: 100%; /* Ensure it takes the full width */
`;

const ToggleNavBarButton = styled.button`
  background: ${({ $navBarIsHidden }) =>
    $navBarIsHidden ? "rgba(0, 0, 0, 0)" : "rgba(0, 0, 0, 0)"};
  border-radius: 0 0 5px 5px;
  cursor: pointer;
  font-size: 16px;
  height: 24px;
  left: 50vw;
  margin-top: 5px;
  position: absolute;
  top: ${({ $navBarIsHidden }) => ($navBarIsHidden ? "0" : "90px")};
  width: 48px;
  z-index: 100;
`;

const SystemHealthContainer = styled.div`
  background: rgba(0, 0, 0, 0);
  bottom: 64px;
  display: flex;
  flex-direction: column;
  font-family: Inter, Oxygen, Roboto, sans-serif;
  left: 64px;
  position: absolute;
`;

const SystemHealthHeader = styled.h2`
  border-bottom: 1px solid white;
  color: white;
`;

const InfoText = styled.span`
  color: white;
`;

const NetworkAveragesContainer = styled.div`
  background: rgba(0, 0, 0, 0);
  bottom: 64px;
  color: white;
  display: flex;
  flex-direction: column;
  font-family: Inter, Oxygen, Roboto, sans-serif;
  left: 400px;
  position: absolute;
`;

const NetworkAveragesHeader = styled.h2`
  border-bottom: 1px solid white;
  color: white;
`;

const MapLegendContainer = styled.div`
  background: rgba(0, 0, 0, 0);
  bottom: 35%;
  display: flex;
  flex-direction: column;
  font-family: Inter, Oxygen, Roboto, sans-serif;
  position: absolute;
  right: 64px;
`;

const MapLegendHeader = styled.h3`
  border-bottom: 1px solid white;
  color: white;
  margin-bottom: 8px;
`;

const MapLegendItem = styled.div`
  color: white;
  display: flex;
  flex-direction: row;
`;

const MapLegendIcon = styled.img`
  border-radius: 50%;
  height: 20px;
  margin: 0 5px 8px 0;
  width: 20px;
`;

const HurricaneWarningContainer = styled.div`
  background: rgba(0, 0, 0, 0);
  bottom: 64px;
  display: flex;
  flex-direction: column;
  font-family: Inter, Oxygen, Roboto, sans-serif;
  position: absolute;
  right: 64px;
`;

const HurricaneWarningHeader = styled.h2`
  border-bottom: 1px solid #c0110f;
  color: #c0110f;
`;

const WildfireWarningContainer = styled.div`
  background: rgba(0, 0, 0, 0);
  bottom: 325px;
  display: flex;
  flex-direction: column;
  font-family: Inter, Oxygen, Roboto, sans-serif;
  left: 64px;
  position: absolute;
`;

const WildfireWarningHeader = styled.h2`
  border-bottom: 1px solid rgb(225, 141, 16);
  color: rgb(225, 141, 16);
`;

const HurricaneImg = styled.img`
  bottom: 150px;
  height: auto;
  position: absolute;
  right: 240px;
  width: 100px;

  animation-name: ${breatheAnimation};
  animation-duration: 2s;
  animation-timing-function: ease;
  animation-delay: 0s;
  animation-iteration-count: 7;
`;

const WildfireImg = styled.img`
  bottom: 400px;
  height: auto;
  left: 120px;
  position: absolute;
  width: 100px;

  animation-name: ${breatheAnimation};
  animation-duration: 2s;
  animation-timing-function: ease;
  animation-delay: 0s;
  animation-iteration-count: 7;
`;

const CNMPLogo = styled.img`
  height: 50px;
  left: 19px;
  opacity: 1;
  position: absolute;
  top: 16px;
  visibility: ${({ $isVisible }) => ($isVisible ? "hidden" : "visible")};
  width: auto;
  z-index: 1000;
`;

const MainDisplay = () => {
  const { user } = useUserContext();

  const [navBarIsHidden, setNavBarIsHidden] = useState(false);
  // TODO: remove temp check on userId
  const [hasHurricaneWarning, setHasHurricaneWarning] = useState(
    user.userId === "tony.k.walters@cnmp.com" ? true : false
  );
  const [hasWildfireWarning, setHasWildfireWarning] = useState(false);

  // Temp useEffect for delaying appearance of Hurricane warning for demos
  useEffect(() => {
    const setWarning = () => {
      setHasHurricaneWarning(true);
    };

    const warningTimeout = setTimeout(setWarning, 10000);

    return () => {
      clearTimeout(warningTimeout);
    };
  }, [setHasHurricaneWarning]);

  // Temp useEffect for delaying appearance of Wildfire warning for demos
  // appears only after Hurricane warning has appeared
  useEffect(() => {
    const setWarning = () => {
      setHasWildfireWarning(true);
    };

    if (hasHurricaneWarning) {
      // TODO: remove temp check on userId
      const warningTimeout = setTimeout(
        setWarning,
        user.userId === "tony.k.walters@cnmp.com" ? 10000 : 15000
      );

      return () => {
        clearTimeout(warningTimeout);
      };
    }
  }, [hasHurricaneWarning, setHasWildfireWarning]);

  const handleToggleNavBar = useCallback(
    (e) => {
      e?.preventDefault();

      setNavBarIsHidden(!navBarIsHidden);
    },
    [navBarIsHidden, setNavBarIsHidden]
  );

  return (
    <Container>
      <NavigationBar isHidden={navBarIsHidden} />

      <CNMPLogo $isVisible={!navBarIsHidden} src={terbineCNMPLogo} />

      <ToggleNavBarButton
        $navBarIsHidden={navBarIsHidden}
        onClick={(e) => handleToggleNavBar(e)}
        type="button"
      >
        {navBarIsHidden ? <Icon src={CaretDown} /> : <Icon src={CaretUp} />}
      </ToggleNavBarButton>

      <MapCanvas>
        <APIProvider apiKey={"AIzaSyBy0cowEjTB0suohBM4h4UdiSz-ng-YPWg"}>
          <Map />
        </APIProvider>

        <SystemHealthContainer>
          <SystemHealthHeader>System Health</SystemHealthHeader>
          <InfoText>
            {user.userId === "tony.k.walters@cnmp.com"
              ? "Northern"
              : "Northeast"}{" "}
            - 97% Operational
          </InfoText>
          <InfoText>
            {user.userId === "tony.k.walters@cnmp.com"
              ? "Bay Area"
              : "Southeast"}{" "}
            - 99% Operational
          </InfoText>
          <InfoText>
            {user.userId === "tony.k.walters@cnmp.com"
              ? "Capitol Region"
              : "Midwest"}{" "}
            - 95% Operational
          </InfoText>
          <InfoText>
            {user.userId === "tony.k.walters@cnmp.com"
              ? "Central"
              : "Northwest"}{" "}
            - 92% Operational
          </InfoText>
          <InfoText>
            {user.userId === "tony.k.walters@cnmp.com"
              ? "Southern"
              : "Southwest"}{" "}
            - 97% Operational
          </InfoText>
        </SystemHealthContainer>

        <NetworkAveragesContainer>
          <NetworkAveragesHeader>Network Averages</NetworkAveragesHeader>
          <InfoText>Session Time - 44 mins</InfoText>
          <InfoText>Peak Output - 130 kW</InfoText>
        </NetworkAveragesContainer>

        <MapLegendContainer>
          <MapLegendHeader>Status</MapLegendHeader>
          <MapLegendItem>
            <MapLegendIcon src={greenIcon} />
            Operational
          </MapLegendItem>
          <MapLegendItem>
            <MapLegendIcon src={yellowIcon} />
            Cyber Security Breach
          </MapLegendItem>
          <MapLegendItem>
            <MapLegendIcon src={redIcon} />
            Faulted
          </MapLegendItem>
          <MapLegendItem>
            <MapLegendIcon src={blueIcon} />
            In Maintenance
          </MapLegendItem>
        </MapLegendContainer>

        {hasHurricaneWarning && user.userId !== "tony.k.walters@cnmp.com" && (
          <>
            <HurricaneWarningContainer>
              <HurricaneWarningHeader>HURRICANE WARNING</HurricaneWarningHeader>
              <InfoText>RECOMMEND LIFTING</InfoText>
              <InfoText>LIMITS AT 13:00 FOR 12 HRS</InfoText>
            </HurricaneWarningContainer>

            <HurricaneImg src={HurricaneIcon} />
          </>
        )}

        {hasWildfireWarning && (
          <>
            {/* TODO: Remove temp check for userId */}
            <WildfireWarningContainer
              style={{
                left:
                  user.userId === "tony.k.walters@cnmp.com" ? "128px" : "64px",
              }}
            >
              <WildfireWarningHeader>WILDFIRE WARNING</WildfireWarningHeader>
              <InfoText>POTENTIAL HAZARD TO REGIONAL FACILITIES</InfoText>
            </WildfireWarningContainer>

            <WildfireImg src={WildfireIcon} />
          </>
        )}
      </MapCanvas>
    </Container>
  );
};

export default MainDisplay;
